import { create } from 'zustand';
import { UserProgress, CareerRecommendation, JobApplication, SkillAssessment } from '../types/dashboard';
import { supabaseClient } from '../config/supabase';
import { useAuthStore } from './authStore';

interface DashboardState {
  progress: UserProgress;
  recommendations: CareerRecommendation[];
  applications: JobApplication[];
  skills: SkillAssessment[];
  analysisHistory: {
    id: string;
    date: string;
    analysis: string;
    quizAnswers: any;
  }[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
  fetchUserDashboard: () => Promise<void>;
  updateProgress: (progress: Partial<UserProgress>) => Promise<void>;
  saveAnalysis: (analysis: string, quizAnswers: any) => Promise<void>;
  clearAnalysisHistory: () => Promise<void>;
  addJobApplication: (application: JobApplication) => Promise<void>;
  updateJobApplication: (id: string, updates: Partial<JobApplication>) => Promise<void>;
  setInitialized: (value: boolean) => void;
  resetDashboard: () => void;
}

interface DashboardData {
  progress: {
    quizCompleted: boolean;
    analysisGenerated: boolean;
    profileCompleted: boolean;
  };
  analysis: string | null;
  stats: {
    generatedAnalyses: number;
    savedJobs: number;
    completedCourses: number;
  };
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  progress: {
    quizCompleted: false,
    cvUploaded: false,
    profileCompleted: 0,
    applicationsSubmitted: 0,
    coursesStarted: 0,
    coursesCompleted: 0
  },
  recommendations: [],
  applications: [],
  skills: [],
  analysisHistory: [],
  loading: false,
  error: null,
  initialized: false,

  setInitialized: (value: boolean) => set({ initialized: value }),

  resetDashboard: () => {
    set({
      progress: {
        quizCompleted: false,
        cvUploaded: false,
        profileCompleted: 0,
        applicationsSubmitted: 0,
        coursesStarted: 0,
        coursesCompleted: 0
      },
      recommendations: [],
      applications: [],
      skills: [],
      analysisHistory: [],
      loading: false,
      error: null,
      initialized: true
    });
  },

  fetchUserDashboard: async () => {
    if (get().loading) return;
    
    try {
      set({ loading: true, error: null });
      
      const { user } = useAuthStore.getState();
      if (!user) {
        throw new Error('Użytkownik nie jest zalogowany');
      }

      // Pobierz wszystkie dane z localStorage
      const cachedAnalysis = localStorage.getItem(`analysis_${user.id}`);
      const cachedQuiz = localStorage.getItem(`quiz_${user.id}`);
      const cachedProgress = localStorage.getItem(`progress_${user.id}`);
      
      // Ustaw stan na podstawie danych z localStorage
      if (cachedAnalysis || cachedQuiz || cachedProgress) {
        set({
          progress: {
            ...get().progress,
            ...(cachedProgress ? JSON.parse(cachedProgress) : {}),
            quizCompleted: !!cachedQuiz,
            cvUploaded: false,
            profileCompleted: cachedQuiz ? 60 : 0,
            applicationsSubmitted: 0,
            coursesStarted: 0,
            coursesCompleted: 0
          },
          analysisHistory: cachedAnalysis ? [{
            id: 'local',
            date: new Date().toISOString(),
            analysis: cachedAnalysis,
            quizAnswers: cachedQuiz ? JSON.parse(cachedQuiz) : null
          }] : [],
          loading: false,
          initialized: true
        });
        
        // Zapisz informację o wygenerowaniu analizy w subscriptionStore
        if (cachedAnalysis) {
          try {
            const { setAnalysisGenerated } = await import('./subscriptionStore');
            setAnalysisGenerated(1);
          } catch (e) {
            console.warn('Failed to update analysis generation count:', e);
          }
        }
      }

      // W tle pobierz dane z bazy
      let analysisHistory = [];
      try {
        const { data: savedAnalysis, error: analysisError } = await supabaseClient
          .from('user_analysis')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5);

        if (analysisError) throw analysisError;

        if (savedAnalysis) {
          analysisHistory = savedAnalysis;
          // Aktualizuj stan tylko jeśli są nowe dane
          set({
            analysisHistory,
            loading: false,
            initialized: true
          });
          
          // Zaktualizuj licznik wygenerowanych analiz
          if (savedAnalysis.length > 0) {
            try {
              const { setAnalysisGenerated } = await import('./subscriptionStore');
              setAnalysisGenerated(savedAnalysis.length);
            } catch (e) {
              console.warn('Failed to update analysis generation count:', e);
            }
          }
        }
      } catch (e) {
        console.warn('Failed to parse analysis history:', e);
      }
    } catch (error: any) {
      console.error('Dashboard initialization error:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Nie udało się załadować danych dashboardu', 
        loading: false,
        initialized: false
      });
      return Promise.reject(error);
    }
  },

  saveAnalysis: async (analysis: string, quizAnswers: any) => {
    try {
      const { user } = useAuthStore.getState();
      if (!user) throw new Error('Użytkownik nie jest zalogowany');

      // Save to Supabase
      const { data, error } = await supabaseClient
        .from('user_analysis')
        .insert([{
          user_id: user.id,
          analysis,
          quiz_answers: quizAnswers,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      set(state => {
        const updatedHistory = [data, ...state.analysisHistory].slice(0, 5);
        return {
          analysisHistory: updatedHistory,
          progress: { ...state.progress, quizCompleted: true }
        };
      });

      // Save to localStorage as backup
      localStorage.setItem(`analysis_${user.id}`, analysis);
      localStorage.setItem(`quiz_${user.id}`, JSON.stringify(quizAnswers));
    } catch (error) {
      console.error('Error saving analysis:', error);
      throw error;
    }
  },

  clearAnalysisHistory: async () => {
    try {
      const { user } = useAuthStore.getState();
      if (!user) throw new Error('Użytkownik nie jest zalogowany');

      localStorage.removeItem(`analysisHistory_${user.id}`);
      set({ analysisHistory: [] });
    } catch (error) {
      console.error('Error clearing analysis history:', error);
      throw error;
    }
  },
  updateProgress: async (progress) => {
    try {
      const { user } = useAuthStore.getState();
      if (!user) throw new Error('User not found');

      // Zapisz postęp w localStorage
      localStorage.setItem(`progress_${user.id}`, JSON.stringify(progress));

      set(state => ({
        progress: { ...state.progress, ...progress }
      }));

      return true; // Indicate success
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error; // Re-throw the error for handling in the component
    }
  },

  addJobApplication: async (application) => {
    set(state => ({
      applications: [...state.applications, application]
    }));
  },

  updateJobApplication: async (id, updates) => {
    set(state => ({
      applications: state.applications.map(app =>
        app.id === id ? { ...app, ...updates } : app
      )
    }));
  }
}));

export const fetchUserDashboard = async (userId: string): Promise<DashboardData | null> => {
  try {
    console.log("Pobieranie danych dashboardu dla użytkownika:", userId);
    
    // Próba pobrania z localStorage jako fallback
    const localAnalysis = localStorage.getItem(`analysis_${userId}`);
    let analysisData = null;
    
    try {
      if (localAnalysis) {
        console.log("Znaleziono analizę w localStorage");
        analysisData = JSON.parse(localAnalysis);
      }
    } catch (parseError) {
      console.error("Błąd parsowania analizy z localStorage:", parseError);
    }
    
    // Próba pobrania z bazy danych, ale jeśli zawiedzie, użyjemy danych z localStorage
    try {
      const { data, error } = await supabaseClient
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .single();
        
      if (error && !error.message?.includes('does not exist')) {
        console.error("Błąd podczas pobierania postępu użytkownika:", error);
      }
      
      // Spróbuj pobrać analizę kariery z tabeli career_analysis
      const { data: dbAnalysisData, error: analysisError } = await supabaseClient
        .from('career_analyses')
        .select('analysis')
        .eq('user_id', userId)
        .single();
        
      if (analysisError && !analysisError.message?.includes('does not exist')) {
        console.error("Błąd podczas pobierania analizy kariery:", analysisError);
      }
      
      // Jeśli udało się pobrać z bazy, użyj tych danych
      if (dbAnalysisData && dbAnalysisData.analysis) {
        try {
          analysisData = typeof dbAnalysisData.analysis === 'string' 
            ? JSON.parse(dbAnalysisData.analysis) 
            : dbAnalysisData.analysis;
        } catch (parseError) {
          console.error("Błąd parsowania analizy z bazy danych:", parseError);
        }
      }
      
      return {
        progress: data || {
          quizCompleted: !!analysisData,
          analysisGenerated: !!analysisData,
          profileCompleted: false
        },
        analysis: analysisData,
        stats: {
          generatedAnalyses: analysisData ? 1 : 0,
          savedJobs: 0,
          completedCourses: 0
        }
      };
    } catch (supabaseError) {
      console.error("Błąd podczas komunikacji z Supabase:", supabaseError);
      
      // Fallback - użyj danych z localStorage
      return {
        progress: {
          quizCompleted: !!analysisData,
          analysisGenerated: !!analysisData,
          profileCompleted: false
        },
        analysis: analysisData,
        stats: {
          generatedAnalyses: analysisData ? 1 : 0,
          savedJobs: 0,
          completedCourses: 0
        }
      };
    }
  } catch (error) {
    console.error("Nieoczekiwany błąd podczas pobierania danych dashboardu:", error);
    return null;
  }
};
